import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo } from 'react';

import { Dropdown, DropdownOption } from '@column/column-ui-kit';

import { Loan, LoanRepository } from '~/repositories';

const loanLabel = (loan: Loan) => loan.description || loan.id;

const loansQueryKey = ['loans', 'list'] as const;
const loanQueryKey = (id: string) => ['loans', 'get', id] as const;

interface UseLoanOptionsConfig {
  value?: string | null;
}

export const useLoanOptions = (config: UseLoanOptionsConfig = {}) => {
  const { value } = config;
  const resolvedId = value ?? undefined;

  const loansQuery = useInfiniteQuery({
    queryKey: loansQueryKey,
    queryFn: ({ pageParam }: { pageParam?: string }) => LoanRepository.getAll({ startingAfter: pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.loans[lastPage.loans.length - 1]?.id;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const loans = useMemo(() => loansQuery.data?.pages.flatMap((page) => page.loans) ?? [], [loansQuery.data]);

  const activeLoanQuery = useQuery({
    queryKey: loanQueryKey(resolvedId!),
    queryFn: () => LoanRepository.get(resolvedId!),
    enabled: Boolean(resolvedId),
    placeholderData: () => loans.find((loan) => loan.id === resolvedId),
    staleTime: 5 * 60_000,
  });

  const activeLoan = activeLoanQuery.data;

  const toOption = useCallback(
    (loan: Loan): DropdownOption => ({
      label: loanLabel(loan),
      value: loan.id,
    }),
    []
  );

  const options = useMemo(() => loans.map(toOption), [loans, toOption]);

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      loans.find((loan) => loan.id === resolvedId) || (activeLoan?.id === resolvedId ? activeLoan : undefined);

    return selected ? [toOption(selected)] : [];
  }, [activeLoan, resolvedId, loans, toOption]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = loansQuery;
  const handleScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    options,
    hiddenOptions,
    handleScrollEnd,
    loans,
  };
};

export interface LoanDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
}

export const LoanDropdown: FC<LoanDropdownProps> = ({
  value,
  onChange,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
}) => {
  const { options, hiddenOptions, handleScrollEnd } = useLoanOptions({ value });

  return (
    <Dropdown
      options={options}
      hiddenOptions={hiddenOptions}
      active={value ?? undefined}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      emptyMessage="No loans found"
      onChange={onChange}
      onScrollEnd={handleScrollEnd}
      hasError={hasError}
      isDisabled={isDisabled}
      placeholder={placeholder}
    />
  );
};
